import React, { ReducerAction } from 'react';

import { classnames } from '@lumapps/classnames';
import { getGroupPublic } from '@lumapps/customer/ducks/selectors';
import { GroupPicker } from '@lumapps/groups-picker/components/GroupPicker';
import { Group } from '@lumapps/groups/types';
import { ExpandableRadioButton } from '@lumapps/lumx-layouts/components/ExpandableRadioButton';
import { mdiAlertCircle, mdiClose, mdiEye } from '@lumapps/lumx/icons';
import { Chip, Icon, RadioButton, RadioGroup, Size } from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { State, reducer, actions } from '../../../ducks/addNavigationItemSlice';
import { NAVIGATION_ADMIN } from '../../../keys';

export interface VisibilitySectionProps {
    /** The modal classname */
    className: string;
    /** The current form state */
    state: State;
    /** Callback to dispatch an action to the form */
    dispatch: React.Dispatch<ReducerAction<typeof reducer>>;
    /** Whether the section should be focuse on init */
    focusOnInit: boolean;
    /** Optional Helper key depending on the node type and resource type */
    helper?: string;
    /** Whether the current node has a linked resource */
    nodeHasResource: boolean;
    /** Callback to prevent the parent dialog to autoclose on key escape typed */
    setPreventAutoClose: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * The visibility section of the Navigation Item Modal. Displays either simply the visibility field in most of the cases,
 * or a radioGroup for the case where the linked resource (if any) has visibility public
 */
export const VisibilitySection: React.FC<VisibilitySectionProps> = ({
    className,
    state,
    dispatch,
    focusOnInit,
    helper,
    nodeHasResource,
    setPreventAutoClose,
}) => {
    const { translateKey } = useTranslate();
    const feedPublic = useSelector(getGroupPublic);

    // Radio management if we are in radiogroup mode
    const [radioSelect, setRadioSelect] = React.useState<'public' | 'restrict'>(
        feedPublic &&
            state.navigationMapItem.visibleBy &&
            state.navigationMapItem.visibleBy.indexOf(feedPublic?.id) >= 0
            ? 'public'
            : 'restrict',
    );

    const onRadioChange = React.useCallback(
        (newValue: 'public' | 'restrict') => {
            setRadioSelect(newValue);

            if (newValue === 'public') {
                // When we switch to public, remove group "All" and add group "Public"
                dispatch(actions.removeVisibleBy(state.feedAll));
                dispatch(actions.addVisibleBy([feedPublic]));
            } else {
                // When we switch to restrict, remove group "Public". The reducer will then add back "All" if necessary
                dispatch(actions.removeVisibleBy(feedPublic));
            }
        },
        [dispatch, feedPublic, state.feedAll],
    );

    // Whether the linked resource is public. In this case, display a radiogroup to manage node's public separately
    const nodeResourceIsPublic =
        nodeHasResource &&
        feedPublic &&
        state.navigationMapItem.pageReference?.groupIds &&
        state.navigationMapItem.pageReference?.groupIds.indexOf(feedPublic.id) >= 0;

    // Callbacks to manage chips in the visibility field
    const clearSelectedGroup = (group: Group) => () => {
        dispatch(actions.removeVisibleBy(group));
    };

    const selectedChipRender = (group: Group, index: number) => {
        // If group name is undefined, the group is unavailable (= deleted or not synced yet)
        const isGroupUnavailable = group.name === undefined;

        return (
            <Chip
                isClickable
                key={index}
                after={<Icon icon={mdiClose} size={Size.xxs} />}
                before={isGroupUnavailable ? <Icon icon={mdiAlertCircle} size={Size.xxs} color="yellow" /> : undefined}
                color={isGroupUnavailable ? 'yellow' : undefined}
                size={Size.s}
                onAfterClick={clearSelectedGroup(group)}
                onClick={clearSelectedGroup(group)}
            >
                {translateKey(isGroupUnavailable ? GLOBAL.UNAVAILABLE_GROUP : group.name)}
            </Chip>
        );
    };

    /**
     * The label fot the group picker:
     * - If node has public resource, the radiogroup is displayed and label shows "Groups"
     * - If node has non public resource, display "Restrict item visibility"
     * - For any other cases, display "Navigation item visibility"
     */
    const pickerLabel = translateKey(
        (nodeResourceIsPublic && GLOBAL.FEEDS) ||
            (nodeHasResource ? NAVIGATION_ADMIN.RESTRICT_ITEM_VISIBILITY : NAVIGATION_ADMIN.ITEM_VISIBILITY),
    );

    const PickerElement = (
        <GroupPicker
            className={classnames({
                [`${className}__visible-by`]: !nodeResourceIsPublic,
                [`${className}__visible-by--resource-public`]: nodeResourceIsPublic,
            })}
            onGroupSelected={(group) => {
                if (state.navigationMapItem.visibleBy?.includes(group.id)) {
                    dispatch(actions.removeVisibleBy(group));
                } else {
                    dispatch(actions.addVisibleBy([group]));
                }
            }}
            focusOnInit={focusOnInit}
            isDisabled={false}
            isMultiple
            values={state.visibleBy}
            icon={mdiEye}
            helper={helper ? translateKey(helper) : undefined}
            showHelper={false}
            selectedChipRender={selectedChipRender}
            placeholder={state.visibleBy?.length > 0 ? undefined : translateKey(GLOBAL.ADD_GROUPS)}
            label={pickerLabel}
            isRequired={!nodeHasResource}
            displayAllGroup={!nodeHasResource}
            displayPublicGroup={!nodeHasResource}
            errorMessage={state.errors.visibleBy ? translateKey(GLOBAL.ERROR_MANDATORY_FIELD) : undefined}
            /**
             * Prevent dialog close while group picker is opened
             * to allow closing picker using escape without closing the dialog
             */
            onOpen={() => setPreventAutoClose(true)}
            onClose={() => setPreventAutoClose(false)}
        />
    );

    return nodeResourceIsPublic ? (
        <RadioGroup className={`${className}__visible-by`}>
            <RadioButton
                isChecked={radioSelect === 'public'}
                label={translateKey(NAVIGATION_ADMIN.NAVIGATION_ITEM_VISIBILITY_IS_PUBLIC)}
                name="visibilityType"
                value="public"
                onChange={onRadioChange}
            />
            <ExpandableRadioButton
                name="visibilityType"
                value="restrict"
                label={translateKey(NAVIGATION_ADMIN.RESTRICT_ITEM_VISIBILITY)}
                isChecked={radioSelect === 'restrict'}
                onChange={onRadioChange}
            >
                {PickerElement}
            </ExpandableRadioButton>
        </RadioGroup>
    ) : (
        PickerElement
    );
};
